import * as React from 'react';

import NextLink from 'next/link';

import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '@/modules/common/lib/utils';

const linkVariants = cva(
  'text-base inline-flex hover:underline active:underline gap-1 items-center justify-center  rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:pointer-block disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'text-black',
        secondary: 'text-gray-500',
        article: 'text-purple',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  }
);

export interface LinkProps
  extends React.LinkHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, href, variant, children, ...props }, ref) => {
    if (!href) {
      return <span className={cn(linkVariants({ variant, className }))}>{children}</span>;
    }

    const isInternal = href.startsWith('/') || href.startsWith('#');
    if (isInternal) {
      return (
        <NextLink
          ref={ref}
          className={cn(linkVariants({ variant, className }))}
          href={href}
          data-testid="link-internal"
          {...props}
        >
          {children}
        </NextLink>
      );
    }

    return (
      <a
        ref={ref}
        className={cn(linkVariants({ variant, className }))}
        target="_blank"
        rel="noreferrer"
        href={href}
        data-testid="link-external"
        {...props}
      >
        {children}
      </a>
    );
  }
);

Link.displayName = 'Link';

export default Link;

export { Link, linkVariants };
